import {
  PermissionCode,
  ProductCode,
  roleAssignmentCodeSchema,
  type UserListQuery,
  type UserListSortBy,
} from '@gb-ticket/shared-types';
import { ChevronLeftIcon, ChevronRightIcon, Loader2Icon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useVoiceCopy } from '@/platform/voice/use-voice';
import { AssignRolesDialog } from '@/platform/features/users/assign-roles-dialog';
import { UsersFilters } from '@/platform/features/users/users-filters';
import { CreateLocalUserDialog } from '@/platform/features/users/create-local-user-dialog';
import { UsersCardList } from '@/platform/features/users/users-card-list';
import { UsersTable, type UserSelection } from '@/platform/features/users/users-table';
import { useUserLookups, useUsers } from '@/platform/features/users/use-users';
import { ApiRequestError } from '@/lib/api';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { adminPanelClassName, adminTableFrameClassName } from '@/platform/features/admin-panel';
import {
  AUTHOR_GRANTS_ADMIN_PATH,
  DEPARTMENTS_ADMIN_PATH,
  TEAMS_ADMIN_PATH,
} from '@/platform/lib/admin';

const EMPTY_QUERY: UserListQuery = {
  page: 1,
  pageSize: 25,
  sortBy: 'lastName',
  sortOrder: 'asc',
  status: 'all',
};

export function UsersPage() {
  const copy = useVoiceCopy();
  const me = useMe();
  const canWrite = hasPermission(me.data, PermissionCode.USERS_MANAGE);
  const intranetOn = me.data?.products?.includes(ProductCode.INTRANET) === true;
  const canAssignRoles = hasPermission(me.data, PermissionCode.USERS_ASSIGN_ROLE);
  const lookups = useUserLookups();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState<UserListQuery>(() => ({
    ...EMPTY_QUERY,
    role: roleFromSearch(searchParams),
  }));
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [assignOpen, setAssignOpen] = useState(false);
  const users = useUsers(query);
  const listKey = [
    query.page,
    query.q,
    query.role,
    query.departmentId,
    query.teamId,
    query.status,
  ].join('\0');
  const skipSelectionReset = useRef(true);

  useEffect(() => {
    if (skipSelectionReset.current) {
      skipSelectionReset.current = false;
      return;
    }
    setSelectedIds(new Set());
    setAssignOpen(false);
  }, [listKey]);

  useEffect(() => {
    const handle = window.setTimeout(() => {
      setQuery((current) => {
        const next = searchInput.trim() || undefined;
        if (current.q === next) {
          return current;
        }
        return { ...current, q: next, page: 1 };
      });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  const totalPages = users.data
    ? Math.max(1, Math.ceil(users.data.total / users.data.pageSize))
    : 1;
  const hasFilters =
    Boolean(query.q) ||
    query.role != null ||
    query.departmentId != null ||
    query.teamId != null ||
    query.status !== 'all';

  function patchQuery(patch: Partial<UserListQuery>) {
    setQuery((current) => ({ ...current, ...patch }));
    if (!('role' in patch)) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (patch.role) {
      next.set('role', patch.role);
    } else {
      next.delete('role');
    }
    setSearchParams(next, { replace: true });
  }

  const selection: UserSelection | undefined = canAssignRoles
    ? {
        selectedIds,
        onToggle(id, selected) {
          setSelectedIds((current) => {
            const next = new Set(current);
            if (selected) {
              next.add(id);
            } else {
              next.delete(id);
            }
            return next;
          });
        },
        onTogglePage(selected) {
          const ids = users.data?.items.map((item) => item.id) ?? [];
          setSelectedIds((current) => {
            const next = new Set(current);
            for (const id of ids) {
              if (selected) {
                next.add(id);
              } else {
                next.delete(id);
              }
            }
            return next;
          });
        },
      }
    : undefined;

  function resetFilters() {
    setSearchInput('');
    setQuery({ ...EMPTY_QUERY });
    const next = new URLSearchParams(searchParams);
    if (!next.has('role')) {
      return;
    }
    next.delete('role');
    setSearchParams(next, { replace: true });
  }

  function onSortChange(sortBy: UserListSortBy) {
    setQuery((current) => ({
      ...current,
      sortBy,
      sortOrder: current.sortBy === sortBy && current.sortOrder === 'asc' ? 'desc' : 'asc',
      page: 1,
    }));
  }

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="Benutzerverwaltung"
        description={
          <>
            Konten suchen und Berechtigungsgruppen zuweisen. Konten werden deaktiviert, nicht
            gelöscht.{' '}
            <Link to={DEPARTMENTS_ADMIN_PATH} className="underline-offset-4 hover:underline">
              Abteilungen
            </Link>{' '}
            und{' '}
            <Link to={TEAMS_ADMIN_PATH} className="underline-offset-4 hover:underline">
              Teams
            </Link>{' '}
            {copy('platform.users.maintain')}
          </>
        }
        action={
          canWrite ? (
            <div className="flex flex-wrap gap-2">
              {intranetOn ? (
                <Button asChild size="sm" variant="outline">
                  <Link to={AUTHOR_GRANTS_ADMIN_PATH}>Beiträge für andere</Link>
                </Button>
              ) : null}
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                Lokalen Benutzer anlegen
              </Button>
            </div>
          ) : null
        }
      />

      <UsersFilters
        query={query}
        lookups={lookups.data}
        searchInput={searchInput}
        total={users.data?.total}
        hasFilters={hasFilters}
        onSearchInputChange={setSearchInput}
        onChange={patchQuery}
        onReset={resetFilters}
      />

      {users.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Benutzer werden geladen …
        </p>
      )}

      {users.isError && (
        <p role="alert" className="text-sm text-destructive">
          {users.error instanceof ApiRequestError
            ? users.error.message
            : 'Die Benutzerliste konnte nicht geladen werden.'}
        </p>
      )}

      {users.data?.total === 0 && !hasFilters && (
        <div className={`${adminPanelClassName} border-dashed text-sm text-muted-foreground`}>
          <p className="font-medium text-foreground">Noch keine Benutzer</p>
          <p className="mt-1">
            Konten entstehen über Microsoft 365 / LDAP oder den Seed.{' '}
            {copy('platform.users.empty.before')}
            <Link to={DEPARTMENTS_ADMIN_PATH} className="underline-offset-4 hover:underline">
              Abteilungen
            </Link>{' '}
            und{' '}
            <Link to={TEAMS_ADMIN_PATH} className="underline-offset-4 hover:underline">
              Teams
            </Link>
            {copy('platform.users.empty.between')}
            <Link to="/helpdesk/integrationen" className="underline-offset-4 hover:underline">
              Integrationen
            </Link>
            .
          </p>
        </div>
      )}

      {users.data && (
        <>
          {(users.data.total > 0 || hasFilters) && (
            <>
              {canAssignRoles && selectedIds.size > 0 ? (
                <div
                  className={`${adminPanelClassName} flex flex-wrap items-center justify-between gap-3`}
                >
                  <p className="text-sm">{selectedIds.size} ausgewählt</p>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => setAssignOpen(true)}>
                      Berechtigungsgruppen zuweisen
                    </Button>
                    <Button type="button" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                      Auswahl aufheben
                    </Button>
                  </div>
                </div>
              ) : null}
              <div className="sm:hidden">
                <UsersCardList items={users.data.items} canWrite={canWrite} selection={selection} />
              </div>
              <div className={`hidden min-w-0 sm:block ${adminTableFrameClassName}`}>
                <UsersTable
                  items={users.data.items}
                  query={query}
                  onSortChange={onSortChange}
                  canWrite={canWrite}
                  selection={selection}
                />
              </div>
            </>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-sm text-muted-foreground">
              {users.data.total === 0
                ? 'Keine Einträge'
                : `${users.data.total} Benutzer · Seite ${users.data.page} von ${totalPages}`}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={query.page <= 1}
                onClick={() => patchQuery({ page: query.page - 1 })}
              >
                <ChevronLeftIcon />
                Zurück
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={query.page >= totalPages}
                onClick={() => patchQuery({ page: query.page + 1 })}
              >
                Weiter
                <ChevronRightIcon />
              </Button>
            </div>
          </div>
        </>
      )}
      <CreateLocalUserDialog
        open={createOpen}
        departments={lookups.data?.departments ?? []}
        onClose={() => setCreateOpen(false)}
      />
      {canAssignRoles && assignOpen ? (
        <AssignRolesDialog
          open
          userIds={[...selectedIds]}
          roles={lookups.data?.roles ?? []}
          onOpenChange={(open) => {
            if (!open) {
              setAssignOpen(false);
            }
          }}
          onAssigned={() => setSelectedIds(new Set())}
        />
      ) : null}
    </section>
  );
}

function roleFromSearch(params: URLSearchParams): string | undefined {
  const parsed = roleAssignmentCodeSchema.safeParse(params.get('role') ?? '');
  return parsed.success ? parsed.data : undefined;
}
